import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { isMasterTenant } from './index';
import type { OrganisationAssignment, OrganisationRole } from '@/contexts/OrganisationContext';

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface UserWithDetails {
  id: string;
  auth_user_id?: string;
  username: string;
  display_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  company_id?: string;
  company_name?: string;
  job_title?: string;
  location?: string;
  credits: number;
  role: string;
  /** Role is provisioning intent (pending_role) — user has never logged in */
  role_pending?: boolean;
  /** Account activation status. undefined where the column doesn't exist. */
  is_active?: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  total_sessions?: number;
  last_session_date?: string;
  organisation_assignments?: OrganisationAssignment[];
}

export interface UserDetailView extends UserWithDetails {
  average_score?: number;
  best_score?: number;
  recent_evaluations?: Array<{
    id: string;
    overall_score: number;
    evaluated_at: string;
    agent_name: string;
  }>;
}

export interface CreateUserData {
  username: string;
  display_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  company_id?: string;
  job_title?: string;
  location?: string;
  role: 'admin' | 'tenant_admin' | 'company_admin' | 'user';
  created_by?: string;
  tenant_id?: string; // Required for white-label tenants
}

export interface UpdateUserData {
  username?: string;
  display_name?: string;
  phone?: string;
  avatar_url?: string;
  company_id?: string;
  job_title?: string;
  location?: string;
  role?: 'admin' | 'tenant_admin' | 'company_admin' | 'user';
  is_active?: boolean;
}

interface OrganisationAssignmentRow {
  id: string;
  profile_id: string;
  role: OrganisationRole;
  branch_id: string | null;
  unit_id: string | null;
  is_primary: boolean;
  status: 'active' | 'inactive';
}

/**
 * Loads the scoped platform access displayed in Boston's people-management
 * surfaces. Job titles deliberately remain on the profile and never influence
 * these permission-bearing assignments.
 */
export async function fetchOrganisationAssignmentsForProfiles(
  profileIds: readonly string[],
  tenantId: string,
): Promise<Record<string, OrganisationAssignment[]>> {
  const uniqueProfileIds = [...new Set(profileIds.filter(Boolean))];
  if (!uniqueProfileIds.length || !tenantId) return {};

  // BSC hierarchy tables are deployment-specific and therefore absent from
  // the shared generated Supabase types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const enterpriseDb = supabase as any;
  const { data: assignmentRows, error: assignmentsError } = await enterpriseDb
    .from('organisation_role_assignments')
    .select('id, profile_id, role, branch_id, unit_id, is_primary, status')
    .eq('tenant_id', tenantId)
    .eq('status', 'active')
    .in('profile_id', uniqueProfileIds);

  if (assignmentsError) {
    throw new Error(`Unable to load organisation access: ${assignmentsError.message}`);
  }

  const assignments = (assignmentRows || []) as OrganisationAssignmentRow[];
  const branchIds = [...new Set(assignments.map((row) => row.branch_id).filter((id): id is string => Boolean(id)))];
  const unitIds = [...new Set(assignments.map((row) => row.unit_id).filter((id): id is string => Boolean(id)))];

  const [branchesResult, unitsResult] = await Promise.all([
    branchIds.length
      ? enterpriseDb
          .from('companies')
          .select('id, name')
          .eq('tenant_id', tenantId)
          .in('id', branchIds)
      : Promise.resolve({ data: [], error: null }),
    unitIds.length
      ? enterpriseDb
          .from('organisation_units')
          .select('id, name')
          .eq('tenant_id', tenantId)
          .in('id', unitIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (branchesResult.error) {
    throw new Error(`Unable to load organisation branches: ${branchesResult.error.message}`);
  }
  if (unitsResult.error) {
    throw new Error(`Unable to load organisation units: ${unitsResult.error.message}`);
  }

  const branchNames = new Map<string, string>(
    (branchesResult.data || []).map((branch: { id: string; name: string }) => [branch.id, branch.name]),
  );
  const unitNames = new Map<string, string>(
    (unitsResult.data || []).map((unit: { id: string; name: string }) => [unit.id, unit.name]),
  );

  const result: Record<string, OrganisationAssignment[]> = Object.fromEntries(
    uniqueProfileIds.map((profileId) => [profileId, []]),
  );

  assignments.forEach((assignment) => {
    result[assignment.profile_id]?.push({
      id: assignment.id,
      role: assignment.role,
      branch_id: assignment.branch_id,
      branch_name: assignment.branch_id ? branchNames.get(assignment.branch_id) || null : null,
      unit_id: assignment.unit_id,
      unit_name: assignment.unit_id ? unitNames.get(assignment.unit_id) || null : null,
      is_primary: assignment.is_primary,
      status: assignment.status,
    });
  });

  Object.values(result).forEach((profileAssignments) => {
    profileAssignments.sort((left, right) => Number(right.is_primary) - Number(left.is_primary));
  });

  return result;
}

export interface CreateOrLinkProfileData {
  email: string;
  display_name: string;
  company_id?: string;
  company_name?: string;
  role: 'admin' | 'company_admin' | 'user';
  created_by?: string;
  /** Required on SSO deployments — the sync-sso-user gate denies login when
   * the profile's tenant doesn't match the deployment tenant. */
  tenant_id?: string;
  job_title?: string;
  phone?: string;
}

// ============================================
// CREATE OR LINK PROFILE (Shared Utility)
// ============================================

/**
 * Creates a new profile or links to an existing one by email.
 * This prevents duplicate profiles when users are invited before SSO login.
 * 
 * @param data - Profile data including email, name, company, and role
 * @returns Result with success status, userId, and whether it was a new or existing profile
 */
export async function createOrLinkProfile(
  data: CreateOrLinkProfileData
): Promise<{ success: boolean; error?: string; userId?: string; isExisting?: boolean }> {
  try {
    const normalizedEmail = data.email.trim().toLowerCase();
    
    // Step 1: Check if profile already exists by email
    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('id, auth_user_id, company_id')
      .eq('email', normalizedEmail)
      .maybeSingle();
    
    if (fetchError) {
      console.error('Error checking for existing profile:', fetchError);
      throw fetchError;
    }
    
    if (existingProfile) {
      // Profile exists - update company and pending_role

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          company_id: data.company_id,
          company_name: data.company_name,
          pending_role: data.role,
          ...(data.tenant_id ? { tenant_id: data.tenant_id } : {}),
          ...(data.job_title !== undefined ? { job_title: data.job_title || null } : {}),
          ...(data.phone !== undefined ? { phone: data.phone || null } : {}),
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingProfile.id);
      
      if (updateError) throw updateError;
      
      // If user already has auth_user_id, update their role in user_roles table
      if (existingProfile.auth_user_id) {
        await supabase.from('user_roles').delete().eq('user_id', existingProfile.auth_user_id);
        await supabase.from('user_roles').insert({
          user_id: existingProfile.auth_user_id,
          role: data.role
        });
      }
      
      return { success: true, userId: existingProfile.id, isExisting: true };
    }
    
    // Step 2: Create new profile with pending_role
    // Prefer a clean username from the email local part (e.g. "evan.xia").
    // Only append a numeric suffix if that username is already taken, so
    // admins see readable @evan.xia handles rather than @evan.xia_l8f2ab.
    const baseUsername = normalizedEmail.split('@')[0].replace(/[^a-zA-Z0-9._-]/g, '');
    let uniqueUsername = baseUsername;
    for (let attempt = 2; attempt <= 20; attempt++) {
      const available = await checkUsernameAvailable(uniqueUsername);
      if (available) break;
      uniqueUsername = `${baseUsername}${attempt}`;
    }
    
    const { data: newProfile, error: insertError } = await supabase
      .from('profiles')
      .insert({
        email: normalizedEmail,
        display_name: data.display_name,
        username: uniqueUsername,
        company_id: data.company_id,
        company_name: data.company_name,
        sso_provider: 'pending',
        pending_role: data.role,
        created_by: data.created_by,
        credits: 0,
        ...(data.tenant_id ? { tenant_id: data.tenant_id } : {}),
        ...(data.job_title ? { job_title: data.job_title } : {}),
        ...(data.phone ? { phone: data.phone } : {}),
      })
      .select()
      .single();
    
    if (insertError) {
      if (insertError.code === '23505') {
        // Duplicate key - race condition, try to fetch the existing profile
        const { data: raceProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', normalizedEmail)
          .single();
        
        if (raceProfile) {
          return { success: true, userId: raceProfile.id, isExisting: true };
        }
        return { success: false, error: 'This email is already registered' };
      }
      throw insertError;
    }
    
    return { success: true, userId: newProfile.id, isExisting: false };
    
  } catch (error) {
    console.error('Error in createOrLinkProfile:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to create/link profile';
    return { success: false, error: errorMessage };
  }
}

// ============================================
// FETCH USERS WITH PAGINATION & FILTERS
// ============================================

export async function fetchUsersWithDetails(filters?: {
  search?: string;
  role?: string;
  company?: string;
  limit?: number;
  offset?: number;
  tenant_id?: string; // For white-label data isolation
}): Promise<{ users: UserWithDetails[]; total: number }> {
  try {
    const isWhiteLabelTenant = filters?.tenant_id && !isMasterTenant(filters.tenant_id);
    
    let mergedProfiles: any[] = [];
    let tenantAdminUserIds = new Set<string>();
    
    // For white-label tenants, use RPC to get users INCLUDING cross-tenant admins
    // This bypasses RLS which would otherwise block seeing admin profiles from other tenants
    if (isWhiteLabelTenant) {
      const { data: rpcUsers, error: rpcError } = await supabase.rpc('get_tenant_users_with_admins', {
        p_tenant_id: filters.tenant_id
      });
      
      if (rpcError) {
        console.error('RPC error:', rpcError);
        throw rpcError;
      }
      
      // Transform RPC results to match expected format
      // (pending_role is returned by the RPC only where the updated function
      // is deployed — dedicated projects like BSC; undefined elsewhere, which
      // degrades gracefully to no pending badge.)
      mergedProfiles = (rpcUsers || []).map((u: any) => ({
        id: u.id,
        auth_user_id: u.auth_user_id,
        email: u.email,
        display_name: u.display_name,
        username: u.username,
        avatar_url: u.avatar_url,
        company_id: u.company_id,
        companies: u.company_name ? { id: u.company_id, name: u.company_name } : null,
        job_title: u.job_title,
        phone: u.phone,
        location: u.location,
        credits: u.credits || 0,
        created_at: u.created_at,
        user_type: u.user_type,
        is_tenant_admin: u.is_tenant_admin,
        pending_role: u.pending_role ?? null,
        is_active: u.is_active ?? null,
      }));
      
      // Build tenant admin set for role lookup
      tenantAdminUserIds = new Set(
        mergedProfiles.filter((p: any) => p.is_tenant_admin).map((p: any) => p.auth_user_id).filter(Boolean)
      );
      
      // Apply search filter (RPC doesn't support it, filter client-side)
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        mergedProfiles = mergedProfiles.filter((p: any) => 
          p.display_name?.toLowerCase().includes(searchLower) ||
          p.email?.toLowerCase().includes(searchLower) ||
          p.username?.toLowerCase().includes(searchLower)
        );
      }
      
      // Apply company filter
      if (filters?.company && filters.company !== 'all') {
        mergedProfiles = mergedProfiles.filter((p: any) => p.company_id === filters.company);
      }
    } else {
      // Master tenant: use standard query (sees all profiles)
      let profileQuery = supabase
        .from('profiles')
        .select('*, companies(id, name)', { count: 'exact' });

      if (filters?.search) {
        profileQuery = profileQuery.or(
          `display_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,username.ilike.%${filters.search}%`
        );
      }

      if (filters?.company && filters.company !== 'all') {
        profileQuery = profileQuery.eq('company_id', filters.company);
      }

      if (filters?.limit && filters?.offset !== undefined) {
        profileQuery = profileQuery.range(filters.offset, filters.offset + filters.limit - 1);
      }

      const { data: profiles, error: profileError } = await profileQuery.order('created_at', {
        ascending: false,
      });

      if (profileError) throw profileError;
      mergedProfiles = profiles || [];
    }

    // Fetch all user roles
    const { data: userRoles } = await supabase.from('user_roles').select('user_id, role');

    // Create role lookup map
    const roleMap = new Map<string, string>(userRoles?.map((ur) => [ur.user_id, ur.role]) || []);

    // Fetch performance stats for all users
    const { data: performanceStats } = await supabase
      .from('team_performance_cache')
      .select('auth_user_id, total_sessions, last_session_date');

    const statsMap = new Map(
      performanceStats?.map((stat) => [stat.auth_user_id, stat]) || []
    );

    // Helper to determine role - tenant admins should show as 'admin'
    const getRole = (profile: any): string => {
      const authId = profile.auth_user_id;
      // Check if user is a tenant admin for this tenant
      if (authId && tenantAdminUserIds.has(authId)) {
        return 'admin';
      }
      // Fall back to user_roles table or pending_role
      return (authId && roleMap.get(authId)) || profile.pending_role || 'user';
    };

    // True when the displayed role is only an INTENT (pending_role set at
    // provisioning) — the user has never logged in, so no assigned role
    // exists yet in user_roles / tenant admin records.
    const isRolePending = (profile: any): boolean => {
      const authId = profile.auth_user_id;
      const hasAssignedRole = Boolean(
        (authId && tenantAdminUserIds.has(authId)) || (authId && roleMap.get(authId))
      );
      return !hasAssignedRole && Boolean(profile.pending_role);
    };

    // Transform and join data
    let users = mergedProfiles.map((profile) => ({
      id: profile.id,
      auth_user_id: profile.auth_user_id,
      username: profile.username,
      display_name: profile.display_name,
      email: profile.email,
      phone: profile.phone || '',
      avatar_url: profile.avatar_url || '',
      company_id: profile.company_id || '',
      company_name: (profile as any).companies?.name || '',
      job_title: profile.job_title || '',
      location: profile.location || '',
      credits: profile.credits || 0,
      role: getRole(profile),
      role_pending: isRolePending(profile),
      is_active: profile.is_active ?? undefined,
      created_at: new Date(profile.created_at).toLocaleDateString(),
      updated_at: profile.updated_at,
      created_by: profile.created_by || '',
      total_sessions: profile.auth_user_id ? statsMap.get(profile.auth_user_id)?.total_sessions || 0 : 0,
      last_session_date: profile.auth_user_id
        ? statsMap.get(profile.auth_user_id)?.last_session_date || ''
        : '',
    }));

    // Apply role filter if specified
    if (filters?.role && filters.role !== 'all') {
      users = users.filter((u) => u.role === filters.role);
    }

    // Total count
    const total = mergedProfiles.length;

    return { users, total };
  } catch (error) {
    console.error('Error fetching users:', error);
    toast.error('Failed to load users');
    return { users: [], total: 0 };
  }
}

// ============================================
// FETCH SINGLE USER BY ID WITH DETAILS
// ============================================

export async function fetchUserById(
  userId: string,
  tenantId?: string,
): Promise<UserDetailView | null> {
  try {
    let profile: any = null;

    if (tenantId && !isMasterTenant(tenantId)) {
      // The tenant user list intentionally includes shared identities whose
      // canonical profile is owned by another tenant. A direct profiles query
      // is correctly hidden by RLS for those rows and PostgREST returns 406
      // when `.single()` sees no visible result. Resolve the detail through
      // the same active-tenant boundary as the list so opening a person is
      // stable and never broadens access beyond the selected tenant.
      const { data: tenantProfiles, error: tenantProfilesError } = await supabase.rpc(
        'get_tenant_users_with_admins',
        { p_tenant_id: tenantId },
      );
      if (tenantProfilesError) throw tenantProfilesError;

      const tenantProfile = (tenantProfiles || []).find((candidate: any) => candidate.id === userId);
      if (!tenantProfile) return null;
      profile = {
        ...tenantProfile,
        companies: tenantProfile.company_name
          ? { id: tenantProfile.company_id, name: tenantProfile.company_name }
          : null,
      };
    } else {
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          companies(id, name)
        `)
        .eq('id', userId)
        .maybeSingle();

      if (profileError) throw profileError;
      profile = data;
    }

    if (!profile) return null;

    // Pending SSO profiles intentionally have no auth_user_id yet. Avoid
    // issuing invalid UUID filters while still returning their account and
    // organisation details to administrators.
    const userRoles = profile.auth_user_id
      ? (await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', profile.auth_user_id)
          .maybeSingle()).data
      : null;
    const stats = profile.auth_user_id
      ? (await supabase
          .from('team_performance_cache')
          .select('total_sessions, average_score, best_score, last_session_date')
          .eq('auth_user_id', profile.auth_user_id)
          .maybeSingle()).data
      : null;
    const evaluations = profile.auth_user_id
      ? (await supabase
          .from('simulation_evaluations')
          .select('id, overall_score, evaluated_at, agent_name')
          .eq('auth_user_id', profile.auth_user_id)
          .order('evaluated_at', { ascending: false })
          .limit(5)).data
      : [];

    return {
      id: profile.id,
      auth_user_id: profile.auth_user_id,
      username: profile.username,
      display_name: profile.display_name,
      email: profile.email,
      phone: profile.phone || '',
      avatar_url: profile.avatar_url || '',
      company_id: profile.company_id || '',
      company_name: (profile as any).companies?.name || '',
      job_title: profile.job_title || '',
      location: profile.location || '',
      credits: profile.credits || 0,
      role: profile.is_tenant_admin ? 'admin' : userRoles?.role || profile.pending_role || 'user',
      role_pending: !userRoles?.role && Boolean(profile.pending_role),
      is_active: profile.is_active ?? undefined,
      created_at: new Date(profile.created_at).toLocaleDateString(),
      updated_at: profile.updated_at,
      created_by: profile.created_by || '',
      total_sessions: stats?.total_sessions || 0,
      average_score: stats?.average_score || 0,
      best_score: stats?.best_score || 0,
      last_session_date: stats?.last_session_date || '',
      recent_evaluations: evaluations || [],
    };
  } catch (error) {
    console.error('Error fetching user details:', error);
    toast.error('Failed to load user details');
    return null;
  }
}

// ============================================
// CHECK USERNAME/EMAIL AVAILABILITY
// ============================================

export async function checkUsernameAvailable(
  username: string,
  excludeUserId?: string
): Promise<boolean> {
  try {
    let query = supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('username', username);

    if (excludeUserId) {
      query = query.neq('id', excludeUserId);
    }

    const { count } = await query;
    return count === 0;
  } catch (error) {
    console.error('Error checking username:', error);
    return false;
  }
}

export async function checkEmailAvailable(email: string, excludeUserId?: string): Promise<boolean> {
  try {
    let query = supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('email', email);

    if (excludeUserId) {
      query = query.neq('id', excludeUserId);
    }

    const { count } = await query;
    return count === 0;
  } catch (error) {
    console.error('Error checking email:', error);
    return false;
  }
}

// ============================================
// CREATE USER
// ============================================

export async function createUser(
  userData: CreateUserData
): Promise<{ success: boolean; error?: string; userId?: string }> {
  try {
    // Check if this is a white-label tenant (not master tenant)
    const isWhiteLabelTenant = userData.tenant_id && !isMasterTenant(userData.tenant_id);

    // For master tenant, pre-check username/email uniqueness (SSO/staging
    // creation flow cannot recover existing users).
    // For white-label, let the edge function decide — it handles existing-user
    // recovery correctly (resets never-signed-in users' password + sends fresh
    // welcome; redirects signed-in users to their existing password).
    if (!isWhiteLabelTenant) {
      const usernameAvailable = await checkUsernameAvailable(userData.username);
      if (!usernameAvailable) {
        return { success: false, error: 'Username already taken' };
      }
      const emailAvailable = await checkEmailAvailable(userData.email);
      if (!emailAvailable) {
        return { success: false, error: 'This email is already registered' };
      }
    }

    if (isWhiteLabelTenant) {
      // WHITE-LABEL TENANT: Use edge function to create user with email/password
      // Generate a temporary password (user can reset later)
      const tempPassword = `Temp${Math.random().toString(36).slice(2, 10)}!`;
      
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        return { success: false, error: 'Not authenticated' };
      }

      // Map role to edge function role parameter
      // tenant_admin -> super_admin (same as creating via TenantDetail)
      // company_admin -> company_admin
      // user -> user
      const edgeFunctionRole = userData.role === 'tenant_admin' ? 'super_admin' : userData.role;

      const response = await supabase.functions.invoke('create-tenant-admin', {
        body: {
          tenant_id: userData.tenant_id,
          email: userData.email,
          password: tempPassword,
          full_name: userData.display_name,
          send_welcome_email: true,
          role: edgeFunctionRole, // Pass the role to edge function
          company_id: userData.company_id, // Pass company for company_admin role
        },
      });

      if (response.error) {
        console.error('Edge function error:', response.error);
        throw new Error(response.error.message || 'Failed to create tenant user');
      }

      const result = response.data;
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create tenant user');
      }

      // Update profile with optional fields only. Do NOT overwrite username
      // or company_id here — for existing users (cross-tenant reuse or
      // never-signed-in recovery) those are already correct and overwriting
      // could trigger a unique-constraint conflict or wipe another tenant's
      // company assignment. The edge function handles those on creation.
      if (result.user_id) {
        const updatePayload: Record<string, unknown> = {};
        if (userData.phone) updatePayload.phone = userData.phone;
        if (userData.avatar_url) updatePayload.avatar_url = userData.avatar_url;
        if (userData.job_title) updatePayload.job_title = userData.job_title;
        if (userData.location) updatePayload.location = userData.location;

        if (Object.keys(updatePayload).length > 0) {
          await supabase.from('profiles').update(updatePayload).eq('id', result.user_id);
        }
      }

      const roleLabel = userData.role === 'tenant_admin' ? 'Tenant Super Admin' :
                       userData.role === 'admin' ? 'Tenant Admin' :
                       userData.role === 'company_admin' ? 'Branch Admin' : 'User';
      toast.success(`✅ ${roleLabel} invited! Check Email History to preview what they received.`);
      return { success: true, userId: result.user_id };
    }

    // MASTER TENANT: Behaviour depends on environment.
    // - Production: SSO-only. Create profile with sso_provider='pending', user logs in via WordPress.
    // - Staging: No SSO. Create auth account directly so user can log in with email/password.
    const isStaging = import.meta.env.VITE_SUPABASE_URL?.includes('vvsynudetoepoghsvrav');

    if (isStaging) {
      // STAGING: Create auth user directly (no SSO available)
      const tempPassword = `Temp${Math.random().toString(36).slice(2, 10)}!`;

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        return { success: false, error: 'Not authenticated' };
      }

      const edgeFunctionRole = userData.role === 'tenant_admin' ? 'super_admin' :
                               userData.role === 'admin' ? 'admin' : userData.role;

      const response = await supabase.functions.invoke('create-tenant-admin', {
        body: {
          tenant_id: userData.tenant_id,
          email: userData.email,
          password: tempPassword,
          full_name: userData.display_name,
          send_welcome_email: true,
          role: edgeFunctionRole,
          company_id: userData.company_id,
          is_master_tenant: true, // Skip email scoping for master tenant
        },
      });

      if (response.error) {
        console.error('Edge function error:', response.error);
        throw new Error(response.error.message || 'Failed to create user');
      }

      const result = response.data;
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to create user');
      }

      if (result.user_id) {
        await supabase
          .from('profiles')
          .update({
            username: userData.username,
            phone: userData.phone,
            avatar_url: userData.avatar_url,
            company_id: userData.company_id,
            job_title: userData.job_title,
            location: userData.location,
            created_by: userData.created_by,
          })
          .eq('id', result.user_id);
      }

      toast.success('User created successfully! Welcome email sent with login credentials.');
      return { success: true, userId: result.user_id };
    }

    // PRODUCTION: SSO flow — create profile pending WordPress SSO link
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .insert({
        // auth_user_id is left null — will be set during WordPress SSO linking
        username: userData.username,
        display_name: userData.display_name,
        email: userData.email,
        phone: userData.phone,
        avatar_url: userData.avatar_url,
        company_id: userData.company_id,
        job_title: userData.job_title,
        location: userData.location,
        membership_level: null,
        credits: 0,
        sso_provider: 'pending',
        created_by: userData.created_by,
        pending_role: userData.role,
        tenant_id: userData.tenant_id,
      })
      .select()
      .single();

    if (profileError) throw profileError;

    toast.success('User created successfully! They can now log in via SSO.');
    return { success: true, userId: profile.id };
  } catch (error) {
    console.error('Error creating user:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to create user';
    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  }
}

// ============================================
// UPDATE USER
// ============================================

export async function updateUser(
  userId: string,
  userData: UpdateUserData
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current user
    const { data: currentUser } = await supabase
      .from('profiles')
      .select('auth_user_id, username')
      .eq('id', userId)
      .single();

    if (!currentUser) {
      return { success: false, error: 'User not found' };
    }

    // Check username availability if changed
    if (userData.username && userData.username !== currentUser.username) {
      const usernameAvailable = await checkUsernameAvailable(userData.username, userId);
      if (!usernameAvailable) {
        return { success: false, error: 'Username already taken' };
      }
    }

    // Sanitize update data - convert undefined to null
    const updateData: any = {
      updated_at: new Date().toISOString(),
    };

    // Only include fields that are provided (not undefined)
    if (userData.username !== undefined) updateData.username = userData.username;
    if (userData.display_name !== undefined) updateData.display_name = userData.display_name;
    if (userData.phone !== undefined) updateData.phone = userData.phone || null;
    if (userData.avatar_url !== undefined) updateData.avatar_url = userData.avatar_url || null;
    if (userData.company_id !== undefined) updateData.company_id = userData.company_id || null;
    if (userData.job_title !== undefined) updateData.job_title = userData.job_title || null;
    if (userData.location !== undefined) updateData.location = userData.location || null;
    if (userData.is_active !== undefined) updateData.is_active = userData.is_active;

    // Update profile with sanitized data
    const { data: updatedProfile, error: profileError } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', userId)
      .select()
      .single();

    if (profileError) {
      console.error('Profile update error:', profileError);
      throw profileError;
    }

    // Update role if provided
    if (userData.role) {
      if (currentUser.auth_user_id) {
        // User is SSO-linked - update user_roles table
        // First delete existing role(s) for this user
        const { error: deleteError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', currentUser.auth_user_id);

        if (deleteError) {
          console.error('Role delete error:', deleteError);
          throw deleteError;
        }

        // Then insert the new role
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: currentUser.auth_user_id,
            role: userData.role,
          });

        if (roleError) {
          console.error('Role insert error:', roleError);
          throw roleError;
        }
      } else {
        // User is not yet SSO-linked - update pending_role in profile
        const { error: pendingRoleError } = await supabase
          .from('profiles')
          .update({ pending_role: userData.role })
          .eq('id', userId);

        if (pendingRoleError) {
          console.error('Pending role update error:', pendingRoleError);
          throw pendingRoleError;
        }
      }
    }

    toast.success('✅ User updated successfully!');
    return { success: true };
  } catch (error) {
    console.error('Error updating user:', error);
    
    // Parse specific error types
    let errorMessage = 'Failed to update user';
    
    if (error instanceof Error) {
      if (error.message.includes('duplicate key')) {
        errorMessage = 'Username or email already exists';
      } else if (error.message.includes('foreign key')) {
        errorMessage = 'Invalid company selection';
      } else if (error.message.includes('violates row-level security')) {
        errorMessage = 'Permission denied';
      } else {
        errorMessage = error.message;
      }
    }
    
    toast.error(`❌ ${errorMessage}`);
    return { success: false, error: errorMessage };
  }
}

// ============================================
// ACTIVATE / DEACTIVATE USER
// ============================================

/**
 * Deactivate (or reactivate) a user instead of deleting them. On dedicated
 * SSO projects (BSC) the sync-sso-user gate blocks login when is_active is
 * false — so deactivation immediately locks the user out while preserving
 * their profile, history and audit trail. Boston requested this over hard
 * deletes. No-op-safe where the is_active column doesn't exist (returns the
 * DB error, surfaced to the admin).
 */
export async function setUserActiveStatus(
  userId: string,
  isActive: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) throw error;
    toast.success(isActive ? 'User activated' : 'User deactivated');
    return { success: true };
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Failed to update status';
    toast.error(`❌ ${msg}`);
    return { success: false, error: msg };
  }
}

// ============================================
// DELETE USER
// ============================================

export async function deleteUser(
  userId: string,
  options: { enterpriseHierarchy?: boolean } = {},
): Promise<{ success: boolean; error?: string }> {
  try {
    if (options.enterpriseHierarchy) {
      const enterpriseDb = supabase as any;
      const { data: removedAssignments, error } = await enterpriseDb.rpc('remove_enterprise_user_access', {
        p_profile_id: userId,
      });
      if (error) throw error;
      if (typeof removedAssignments !== 'number' || removedAssignments < 1) {
        throw new Error('No organisation access was removed');
      }

      toast.success('User removed from your organisation');
      return { success: true };
    }

    // Get auth_user_id first
    const { data: user } = await supabase
      .from('profiles')
      .select('auth_user_id')
      .eq('id', userId)
      .single();

    if (!user) {
      return { success: false, error: 'User not found' };
    }

    if (user.auth_user_id) {
      // Delete from tenant_admins (if exists) - CRITICAL for accurate counts
      await supabase.from('tenant_admins').delete().eq('user_id', user.auth_user_id);
      
      // Delete role (if exists)
      await supabase.from('user_roles').delete().eq('user_id', user.auth_user_id);
    }

    // Delete profile
    const { error: deleteError } = await supabase.from('profiles').delete().eq('id', userId);

    if (deleteError) throw deleteError;

    toast.success('✅ User deleted successfully!');
    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  }
}

// ============================================
// USER STATS
// ============================================

export async function fetchUserStats(filters?: { tenant_id?: string; company_id?: string }) {
  try {
    const isWhiteLabelTenant = filters?.tenant_id && !isMasterTenant(filters.tenant_id);
    
    let total = 0;
    let adminCount = 0;
    
    if (isWhiteLabelTenant && filters?.tenant_id) {
      // Use RPC for consistent counting with User Management page
      const { data: rpcUsers, error: rpcError } = await supabase.rpc('get_tenant_users_with_admins', {
        p_tenant_id: filters.tenant_id
      });
      
      if (rpcError) {
        console.error('RPC error in user stats:', rpcError);
        return { total: 0, admins: 0, users: 0 };
      }
      
      // Filter by company_id if provided (for branch admins)
      let filteredUsers = rpcUsers || [];
      if (filters?.company_id) {
        filteredUsers = filteredUsers.filter((u: any) => u.company_id === filters.company_id);
      }
      
      total = filteredUsers.length;
      
      // Count admins: users with is_tenant_admin=true OR user_type contains 'admin'
      // Also check user_roles table for accurate role info
      // Use filteredUsers for accurate stats when company filtering is active
      const authUserIds = filteredUsers.map((u: any) => u.auth_user_id).filter(Boolean) || [];
      
      if (authUserIds.length > 0) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('user_id, role')
          .in('user_id', authUserIds);
        
        // Count users with admin role in user_roles
        const adminUserIds = new Set(
          roleData?.filter((r) => r.role === 'admin').map(r => r.user_id) || []
        );
        
        // Also count tenant_admins as admins (only from filtered users)
        const tenantAdminIds = new Set(
          filteredUsers.filter((u: any) => u.is_tenant_admin).map((u: any) => u.auth_user_id) || []
        );
        
        // Merge both sets
        adminUserIds.forEach(id => tenantAdminIds.add(id));
        adminCount = tenantAdminIds.size;
      }
    } else {
      // Master tenant: count all profiles
      const { count } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
      total = count || 0;
      
      // Count all admins from user_roles
      const { count: adminRoleCount } = await supabase
        .from('user_roles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'admin');
      
      adminCount = adminRoleCount || 0;
    }
    
    const userCount = total - adminCount;

    return {
      total,
      admins: adminCount,
      users: Math.max(0, userCount),
    };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    return { total: 0, admins: 0, users: 0 };
  }
}
